import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';

import Select from "@/Components/Inputs/Select";
import SelectOption from "@/Components/Inputs/SelectOption";
import { ChevronLeftIcon, ChevronRightIcon } from "@/Icons";

const Calendar = ({ date,
    today,
    selectedDate,
    singleDate,
    setDate,
    startDate,
    endDate,
    startTime,
    endTime,
    inBetweenDays,
    setInBetweenDays,
    onSetSelectedDate,
    onSetSelectedDates,
    disablePastDates,
    disableFutureDates = true }) => {

    const getYearsDownto1970 = year => {
        const years = [];
        let currentLoopYear = year;

        if (!disableFutureDates) {
            currentLoopYear = 2099;
        }

        while (currentLoopYear > 1970) {
            years.push(currentLoopYear);
            currentLoopYear -= 1;
        }

        return years;
    };

    const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const DAYS_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const DAYS_OF_THE_WEEK = ['SU', 'M', 'T', 'W', 'TH', 'F', 'SA'];
    const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const getStartDayOfMonth = useCallback((date, startDate) => {
        return startDate === 0 ? 7 : new Date(date.getFullYear(), date.getMonth(), 2).getDay();
    }, []);
    const isLeapYear = year => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    const [day, setDay] = useState(date.getDate());
    const [month, setMonth] = useState(date.getMonth());
    const [year, setYear] = useState(date.getFullYear());
    const [start, setStart] = useState(getStartDayOfMonth(date));
    const [years, setYears] = useState(getYearsDownto1970(year));
    const [enablePrevious, setEnablePrevious] = useState(true);
    const newDays = [null, null, null, null, null, null, 1];

    const getDateRange = useCallback(() => {
        const newInBetweenDays = [];

        var dt = new Date(startDate);
        for (dt.setDate(dt.getDate() + 1); dt < endDate; dt.setDate(dt.getDate() + 1)) {
            newInBetweenDays.push(new Date(dt));
        }

        setInBetweenDays(newInBetweenDays);
    }, [endDate, setInBetweenDays, startDate]);

    useEffect(() => {
        setDay(date.getDate());
        setMonth(date.getMonth());
        setYear(date.getFullYear());
        setStart(getStartDayOfMonth(date));

        if (singleDate && disablePastDates) {
            if (new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) || new Date(date).setHours(0, 0, 0, 0) === new Date(today).setHours(0, 0, 0, 0)) {
                return setEnablePrevious(false);
            }

            return setEnablePrevious(true);
        }
    }, [date, today, singleDate, disablePastDates, getStartDayOfMonth]);

    useEffect(() => {
        if (startDate && endDate) {
            getDateRange(startDate, endDate);
        }
    }, [startDate, endDate, getDateRange]);

    const days = isLeapYear(year) ? DAYS_LEAP : DAYS;


    const convertDate = selectedDate => {
        const date = new Date(selectedDate),
            month = ("0" + (date.getMonth() + 1)).slice(-2),
            day = ("0" + date.getDate()).slice(-2);

        if (singleDate) {
            return [date.getFullYear(), month, day].join("/");
        } else {
            return moment(selectedDate).isValid() ? [date.getFullYear(), month, day].join("/") : null;
        }
    }

    const handleSelectDate = selectedDate => {
        onSetSelectedDate({ singleDate: selectedDate });
    }

    const handleSetDates = selectedDate => {
        if (startDate && endDate) {
            onSetSelectedDates({ startDate: moment(selectedDate).format("YYYY-MM-DDT" + startTime), endDate: null });
            setInBetweenDays([]);
        } else {
            if (startDate && !endDate) {
                onSetSelectedDates({ startDate: moment(startDate).format("YYYY-MM-DDT" + startTime), endDate: moment(selectedDate).format("YYYY-MM-DDT" + endTime) });
            } else {
                onSetSelectedDates({ startDate: moment(selectedDate).format("YYYY-MM-DDT" + startTime), endDate: null });
                setInBetweenDays([]);
            }
        }
    };

    const handleChangeYear = event => {
        if (event && event.target) {
            const selectedYear = event.target.value;
            setYear(selectedYear);
            setDate(new Date(selectedYear, month, day));
        }
    };

    const renderYearsOptions = () => {
        return years && Array.isArray(years) && years.map((year, index) => {
            return (
                <SelectOption key={year.toString() + index} value={year}>
                    {year}
                </SelectOption>
            );
        });
    };

    const handleNext = () => {
        const newDate = new Date(year, month + 1, day);
        const newYear = newDate.getFullYear();
        setDate(newDate);
        if (newYear > year && !years.includes(newYear)) setYears([newYear, ...years]);
    };

    const currentMonth = today.getMonth();
    const currentDate = today.getDate();
    const currentYear = today.getFullYear();
    let isPast = false;

    let enableNext = false;

    if (disableFutureDates) {
        if (currentYear <= year) {
            if (currentMonth > month) {
                enableNext = true;
            }
        } else {
            enableNext = true;
        }
    } else {
        enableNext = true;
    }


    return (
        <React.Fragment>
            <div className="calendar-header">
                {singleDate &&
                    <div className="calendar-header-years">
                        <Select
                            value={year}
                            onChange={handleChangeYear}
                            className="calendar-header-years-select"
                            disabled={singleDate && disablePastDates}
                        >
                            {renderYearsOptions()}
                        </Select>
                    </div>
                }
                <div className="calendar-header-month">
                    {MONTHS[month]} {!singleDate && year}
                </div>
                <div className="calendar-header-buttons">
                    <button className="calendar-header-button calendar-header-button-previous" disabled={!enablePrevious} onClick={() => enablePrevious ? setDate(new Date(year, month - 1, day)) : null}>
                        <ChevronLeftIcon />
                    </button>
                    {
                        singleDate || enableNext ? (
                            <button className="calendar-header-button calendar-header-button-previous" onClick={handleNext}>
                                <ChevronRightIcon />
                            </button>
                        ) : (
                            <button className="calendar-header-button calendar-header-button-next" disabled>
                                <ChevronRightIcon />
                            </button>
                        )
                    }
                </div>
            </div>

            <div className="calendar-body">
                <div className="calendar-content">
                    {DAYS_OF_THE_WEEK.map((d) => (
                        <div className="calendar-body-week" key={d}>
                            <span style={{ color: '#7f8fa4' }}>{d}</span>
                        </div>
                    ))}
                </div>
                <div className="calendar-content">
                    {
                        start === 0 && (
                            newDays.map((newDay, index) => {
                                const value = year + '/' + (month + 1) + '/' + newDay;
                                const valueGMT = new Date(value);
                                const newCalendarValue = convertDate(valueGMT);
                                const newSelectedValue = convertDate(selectedDate);
                                const newStartDateValue = convertDate(startDate);
                                const newEndDateValue = convertDate(endDate);
                                if (singleDate && disablePastDates) isPast = new Date(newCalendarValue).setHours(0, 0, 0, 0) < new Date(today).setHours(0, 0, 0, 0);

                                let inside;
                                if (inBetweenDays) {
                                    inBetweenDays.forEach(inBetweenDay => {
                                        if (convertDate(inBetweenDay) === convertDate(valueGMT)) {
                                            inside = true;
                                        }
                                    });
                                }

                                let classes;

                                if (onSetSelectedDates) {
                                    classes = `
                                        ${inside ? 'bg-primary-100 text-primary font-bold ' : ''}
                                        ${month === currentMonth && newDay === currentDate ? 'border-2 border-[#E0ECF5] !text-purple-1 font-bold ' : ''}
                                        ${newCalendarValue === newStartDateValue ? 'bg-primary-100 text-primary font-bold rounded-l-sm border-primary rounded-sm ' : ''}
                                        ${newEndDateValue && newCalendarValue === newEndDateValue ? 'bg-primary-100 text-primary font-bold rounded-r-sm border border-primary rounded-sm ' : ''}
                                        ${!enableNext && currentMonth <= month && currentDate < newDay ? 'text-gray-300 cursor-not-allowed' : ''}
                                    `;
                                } else {
                                    classes = `
                                        ${inside ? 'bg-primary-100 text-primary font-bold ' : ''}
                                        ${month === currentMonth && newDay === currentDate ? 'border-2 border-[#E0ECF5] !text-purple-1 font-bold' : ''}
                                        ${newCalendarValue === newStartDateValue ? 'bg-primary-100 text-primary font-bold rounded-l-sm border-primary rounded-sm ' : ''}
                                        ${newCalendarValue === newEndDateValue ? 'bg-primary-100 text-primary font-bold rounded-r-sm border border-primary rounded-sm' : ''}
                                    `;
                                }

                                // Display for Single Date Picker
                                if (singleDate) {
                                    return (
                                        <div
                                            className={`
                                                calendar-body-days
                                                ${newDay > 0 ? '' : 'pointer-events-none'}
                                                ${disablePastDates && isPast && 'cursor-not-allowed text-gray-3'}
                                                ${newCalendarValue === newSelectedValue ? 'bg-purple-2 !text-purple-1 border font-bold rounded-sm border-purple-1' : ''}
                                                ${month === currentMonth && newDay === currentDate ? 'border border-[#E0ECF5] !text-purple-1 font-bold' : ''}
                                            `}
                                            key={'enabled' + index}
                                            onClick={() => {
                                                if (disablePastDates && isPast) return null;
                                                return handleSelectDate(valueGMT);
                                            }}
                                            date-value={newCalendarValue}
                                            tabIndex={newDay ? 0 : -1}
                                        >
                                            {newDay ? newDay : ''}
                                        </div>
                                    );
                                } else {
                                    return (
                                        !enableNext && currentMonth <= month && currentDate < newDay ?
                                            <div className={`calendar-body-days ${classes} ${(newDay > 0 ? '' : 'pointer-events-none')}`}
                                                key={'disabled' + index}
                                                date-value={newCalendarValue}
                                                tabIndex={newDay ? 0 : -1}
                                            >
                                                {newDay ? newDay : ''}
                                            </div> : <div
                                                className={`calendar-body-days ${classes} ${(newDay > 0 ? '' : 'pointer-events-none')}`}
                                                key={'enabled' + index}
                                                onClick={() => handleSetDates(valueGMT)}
                                                date-value={newCalendarValue}
                                                tabIndex={newDay ? 0 : -1}
                                            >
                                                {newDay ? newDay : ''}
                                            </div>
                                    );
                                }
                            })
                        )
                    }


                    {Array(days[month] + (start - 1)).fill(null).map((_, index) => {
                        const d = index - (start - 2);
                        const value = year + '/' + (month + 1) + '/' + d;
                        const valueGMT = new Date(value);
                        const newCalendarValue = convertDate(valueGMT);
                        const newSelectedValue = convertDate(selectedDate);
                        const newStartDateValue = convertDate(startDate);
                        const newEndDateValue = convertDate(endDate);
                        if (singleDate && disablePastDates) isPast = new Date(newCalendarValue).setHours(0, 0, 0, 0) < new Date(today).setHours(0, 0, 0, 0);

                        let inside;
                        if (inBetweenDays) {
                            inBetweenDays.forEach(inBetweenDay => {
                                if (convertDate(inBetweenDay) === convertDate(valueGMT)) {
                                    inside = true;
                                }
                            });
                        }

                        let classes;

                        if (onSetSelectedDates) {
                            classes = `
                                ${inside ? 'bg-primary-100 text-primary font-bold ' : ''}
                                ${month === currentMonth && d === currentDate ? 'border-2 border-[#E0ECF5] !text-purple-1 font-bold' : ''}
                                ${newCalendarValue === newStartDateValue ? 'bg-primary-100 text-primary font-bold rounded-l-sm border-primary rounded-sm ' : ''}
                                ${newEndDateValue && newCalendarValue === newEndDateValue ? 'bg-primary-100 text-primary font-bold rounded-r-sm border border-primary rounded-sm 111111 ' : ''}
                                ${!enableNext && currentMonth <= month && currentDate < d ? 'text-gray-300 cursor-not-allowed' : ''}
                            `;
                        } else {
                            classes = `
                                ${inside ? 'bg-primary-100 text-primary font-bold ' : ''}
                                ${month === currentMonth && d === currentDate ? 'border-2 border-[#E0ECF5] !text-purple-1 font-bold' : ''}
                                ${newCalendarValue === newStartDateValue ? 'bg-primary-100 text-primary font-bold rounded-l-sm border-primary rounded-sm ' : ''}
                                ${newCalendarValue === newEndDateValue ? 'bg-primary-100 text-primary font-bold rounded-r-sm border border-primary rounded-sm 222222' : ''}
                            `;
                        }

                        // Display for Single Date Picker
                        if (singleDate) {
                            return (
                                <div
                                    className={`
                                        calendar-body-days
                                        ${d > 0 ? '' : 'pointer-events-none'}
                                        ${disablePastDates && isPast && 'cursor-not-allowed text-gray-3'}
                                        ${newCalendarValue === newSelectedValue ? 'bg-purple-2 !text-purple-1 border font-bold rounded-sm border-purple-1' : ''}
                                        ${month === currentMonth && d === currentDate ? ' border-[#E0ECF5] !text-purple-1 font-bold' : ''}
                                    `}
                                    key={'enabled' + index}
                                    onClick={() => {
                                        if (disablePastDates && isPast) return null;
                                        return handleSelectDate(valueGMT);
                                    }}
                                    date-value={newCalendarValue}
                                    tabIndex={d > 0 ? 0 : -1}
                                    role={d > 0 ? 'button' : 'div'}
                                >
                                    {d > 0 ? d : ''}
                                </div>
                            );
                        } else {
                            return (
                                !enableNext && currentMonth <= month && currentDate < d ?
                                    <div className={`calendar-body-days ${classes} ${(d > 0 ? '' : 'pointer-events-none')}`}
                                        key={'disabled' + index}
                                        date-value={newCalendarValue}
                                        tabIndex={d > 0 ? 0 : -1}
                                        role={d > 0 ? 'button' : 'div'}
                                    >
                                        {d > 0 ? d : ''}
                                    </div> : <div
                                        className={`calendar-body-days ${classes} ${(d > 0 ? '' : 'pointer-events-none')}`}
                                        key={'enabled' + index}
                                        onClick={() => handleSetDates(valueGMT)}
                                        date-value={newCalendarValue}
                                        tabIndex={d > 0 ? 0 : -1}
                                        role={d > 0 ? 'button' : 'div'}
                                    >
                                        {d > 0 ? d : ''}
                                    </div>
                            );
                        }
                    })}
                </div>
            </div>
        </React.Fragment>
    );
};

export default Calendar;